import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import {
  dismissTownViewTutorialPrompt,
  isTownViewTutorialPromptDismissed,
  TOWN_VIEW_TUTORIAL_REPLAY_EVENT,
} from './townViewTutorialStorage.js';
import TownViewTutorialSpotlight from './TownViewTutorialSpotlight.js';
import { buildTownViewTutorialSteps } from './townViewTutorialSteps.js';
import { useUser } from '../../UserContext.js';

const PROMPT_OVERLAY_SX = {
  position: 'fixed',
  inset: 0,
  zIndex: 10000,
  bgcolor: 'rgba(0, 0, 0, 0.58)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 2,
};

const CARD_SX = {
  bgcolor: 'background.paper',
  borderRadius: 2,
  p: 3,
  maxWidth: 520,
  width: '100%',
  boxShadow: 24,
};

function TownViewTutorialOverlay({ ready, scopeKey }) {
  const { user } = useUser();
  const shareWorkups = Boolean(user?.shareWorkups);
  const [phase, setPhase] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  const tutorialSteps = useMemo(
    () => buildTownViewTutorialSteps({ shareWorkups }),
    [shareWorkups],
  );
  const [activeSteps, setActiveSteps] = useState(tutorialSteps);

  useEffect(() => {
    if (!ready || !scopeKey) {
      setPhase(null);
      return;
    }
    if (isTownViewTutorialPromptDismissed(scopeKey)) {
      setPhase(null);
      return;
    }
    setPhase('prompt');
    setStepIndex(0);
    setDoNotShowAgain(false);
  }, [ready, scopeKey]);

  const closeOverlay = (persistDismissal = false) => {
    if (persistDismissal) {
      dismissTownViewTutorialPrompt(scopeKey);
    }
    setPhase(null);
  };

  const handleDecline = () => {
    closeOverlay(doNotShowAgain);
  };

  const handleAcceptTutorial = useCallback(() => {
    const available = tutorialSteps.filter((step) => step.resolveTarget?.());
    setActiveSteps(available.length ? available : tutorialSteps);
    setPhase('tutorial');
    setStepIndex(0);
  }, [tutorialSteps]);

  useEffect(() => {
    window.addEventListener(TOWN_VIEW_TUTORIAL_REPLAY_EVENT, handleAcceptTutorial);
    return () => window.removeEventListener(TOWN_VIEW_TUTORIAL_REPLAY_EVENT, handleAcceptTutorial);
  }, [handleAcceptTutorial]);

  const handleFinishTutorial = () => {
    closeOverlay(false);
  };

  const handleNext = () => {
    if (stepIndex >= activeSteps.length - 1) {
      handleFinishTutorial();
      return;
    }
    setStepIndex((i) => i + 1);
  };

  const handleBack = () => {
    setStepIndex((i) => Math.max(0, i - 1));
  };

  if (!phase) return null;

  if (phase === 'tutorial') {
    return (
      <TownViewTutorialSpotlight
        steps={activeSteps}
        stepIndex={stepIndex}
        onBack={handleBack}
        onNext={handleNext}
        onDone={handleFinishTutorial}
      />
    );
  }

  const blockBackgroundInteraction = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return createPortal(
    <Box
      sx={{ ...PROMPT_OVERLAY_SX, pointerEvents: 'auto' }}
      role="presentation"
      onClick={blockBackgroundInteraction}
      onMouseDown={blockBackgroundInteraction}
      onPointerDown={blockBackgroundInteraction}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="town-view-tutorial-title"
        sx={CARD_SX}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Typography id="town-view-tutorial-title" variant="h6" component="h2" sx={{ mb: 2 }}>
          Town portal
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          This view allows you to directly resolve and respond to offers sent. Would you like to
          see a tutorial of how this functionality works?
        </Typography>
        <FormControlLabel
          sx={{ mb: 2, ml: 0 }}
          control={
            <Checkbox
              checked={doNotShowAgain}
              onChange={(e) => setDoNotShowAgain(e.target.checked)}
              size="small"
            />
          }
          label="Do not show this again"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={handleDecline}>
            No
          </Button>
          <Button variant="contained" onClick={handleAcceptTutorial}>
            Yes
          </Button>
        </Box>
      </Box>
    </Box>,
    document.body,
  );
}

export default TownViewTutorialOverlay;
